import MainLayout from "@/components/layout/MainLayout";
import { SEO } from "@/components/seo/SEO";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { IntegrationSettings } from "@/components/settings/IntegrationSettings";
import { UserPermissions } from "@/components/settings/UserPermissions";
import { CustomizationSettings } from "@/components/settings/CustomizationSettings";
import { SystemLogs } from "@/components/settings/SystemLogs";
import { AdminSettings } from "@/components/settings/AdminSettings";
import { HealthStatus } from "@/components/data/HealthStatus";
import { DataControls } from "@/components/legal/DataControls";
import { ScheduleReportDialog } from "@/components/reports/ScheduleReportDialog";
import { ProductTourCard } from "@/components/settings/ProductTourCard";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  Link2,
  Mail,
  ShieldCheck,
  SlidersHorizontal,
  Lock,
  ScrollText,
} from "lucide-react";

const TABS = [
  { value: "health", label: "System Health", icon: Activity },
  { value: "integrations", label: "Integrations", icon: Link2 },
  { value: "reports", label: "Report Emails", icon: Mail },
  { value: "permissions", label: "Permissions", icon: ShieldCheck },
  { value: "customization", label: "Customization", icon: SlidersHorizontal },
  { value: "privacy", label: "Privacy", icon: Lock },
  { value: "logs", label: "System Logs", icon: ScrollText },
];

const Settings = () => {
  return (
    <MainLayout>
      <SEO title="Settings — Veylo AI" description="Manage system health, integrations, report emails, permissions, customization, privacy controls and system logs." path="/settings" />
      <div className="space-y-6">
        <header>
          <h1 className="text-3xl font-bold">Settings</h1>
          <p className="text-muted-foreground">
            Configure your workspace, manage connections and monitor how the system is running.
          </p>
        </header>

        <Tabs defaultValue="health" className="space-y-6">
          <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
            {TABS.map(({ value, label, icon: Icon }) => (
              <TabsTrigger key={value} value={value} className="gap-2">
                <Icon className="h-4 w-4" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="health" className="space-y-6">
            <HealthStatus />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <AdminSettings />
              <ProductTourCard />
            </div>
          </TabsContent>

          <TabsContent value="integrations">
            <IntegrationSettings />
          </TabsContent>

          <TabsContent value="reports">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Mail className="h-5 w-5 text-primary" />
                  Report Emails
                </CardTitle>
                <CardDescription>
                  Get your analytics, workflow or prediction reports delivered to your inbox on a
                  schedule. Saved schedules run automatically in the background.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ScheduleReportDialog />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="permissions">
            <UserPermissions />
          </TabsContent>

          <TabsContent value="customization">
            <CustomizationSettings />
          </TabsContent>

          <TabsContent value="privacy">
            <DataControls />
          </TabsContent>

          <TabsContent value="logs">
            <SystemLogs />
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
};

export default Settings;
